// Publishing calendar — selection + grouping view-state (2026-07-29). The calendar
// is SELECTION-SCOPED: it shows nothing until account(s) are picked, then exactly
// those. The selection + the left-menu grouping choice persist SESSION-scoped
// (like the inbox feed controls) so returning to the calendar keeps the view.
// Pure + guarded so it's unit-testable and SSR-safe.

// How the left menu is ORGANISED (a pure display control — it never filters the
// calendar, colours chips, or changes the grid layout):
//   brand — under marque headers (brandName / computeBrandAncestorMap)
//   group — under owner-group headers (nearest group-type ancestor)
//   flat  — one flat alphabetical list ("No Group" in the switch)
export type MenuGroupMode = 'brand' | 'group' | 'flat';

const SELECTION_KEY = 'kyck-publishing-calendar-selection';
const GROUP_MODE_KEY = 'kyck-publishing-calendar-groupmode';

const GROUP_MODES: MenuGroupMode[] = ['brand', 'group', 'flat'];

function session(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null; // access can throw in locked-down environments
  }
}

export function loadSelectedAccountIds(): string[] {
  const raw = session()?.getItem(SELECTION_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Dedupe + keep only strings — tolerate a corrupted/legacy value.
    return [...new Set(parsed.filter((v): v is string => typeof v === 'string'))];
  } catch {
    return [];
  }
}

export function saveSelectedAccountIds(ids: Iterable<string>): void {
  const unique = [...new Set(ids)];
  try {
    session()?.setItem(SELECTION_KEY, JSON.stringify(unique));
  } catch {
    /* best-effort */
  }
}

export function normalizeGroupMode(value: string | null | undefined): MenuGroupMode {
  return GROUP_MODES.includes(value as MenuGroupMode) ? (value as MenuGroupMode) : 'brand';
}

export function loadGroupMode(): MenuGroupMode {
  return normalizeGroupMode(session()?.getItem(GROUP_MODE_KEY));
}

export function saveGroupMode(mode: MenuGroupMode): void {
  try {
    session()?.setItem(GROUP_MODE_KEY, mode);
  } catch {
    /* best-effort */
  }
}

/**
 * The account to PRE-SELECT when opening the composer from the calendar (header
 * "Create post" + the per-day icon). Only unambiguous when EXACTLY ONE account is
 * selected — with zero or many, the composer opens with an empty account picker.
 */
export function selectedAccountForCreate(selectedIds: readonly string[]): string | null {
  return selectedIds.length === 1 ? selectedIds[0]! : null;
}
